import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition
} from 'react'

import { AiAgentSection } from '@inno-tools/container-bridge'
import { Button } from '@inno-ui/button'
import {
  EmptyState,
  EmptyStateSizes,
  Illustrations
} from '@inno-ui/empty-state'
import { MagnifyerLineIcon } from '@inno-ui/icons'
import { TextInput, TextInputSizes } from '@inno-ui/text-input'
import { hasAppAccess } from 'modules/app-registry/hooks/hasAppAccess'
// imported from each widget's own subpath, not the top-level `../components`
// barrel — that barrel also re-exports LivechatWidgets, which pulls in a
// long chain (app-registry -> routes -> DesktopRouter -> MainPage ->
// SidebarWidgets) that leads right back to widgetsConfig.ts, creating a
// module-init cycle (verified with madge).
import {
  AiArtifactMarketplaceItems,
  ReviewSection,
  openAgentChat
} from 'modules/desktop/modules/widgets/components/AiArtifacts'
import { MarketplacePageHeader } from 'modules/desktop/modules/widgets/components/MarketplacePageHeader'
import { MarketplaceTileShell } from 'modules/desktop/modules/widgets/components/MarketplaceTileShell'
import { ViewportMount } from 'modules/desktop/modules/widgets/components/ViewportMount/ViewportMount'
import { WidgetWrapperProps } from 'modules/desktop/modules/widgets/components/WidgetWrapper'
import { WidgetsErrorBoundary } from 'modules/desktop/modules/widgets/components/WidgetsErrorBoundary'
import {
  DEFAULT_WIDGET_SIZE,
  WIDGET_NATURAL_HEIGHTS,
  WIDGET_NATURAL_WIDTH
} from 'modules/desktop/modules/widgets/constants/widgetDimensions'
import { WIDGETS_CONFIG } from 'modules/desktop/modules/widgets/constants/widgetsConfig'
import { useDesktopWidgets } from 'modules/desktop/modules/widgets/hooks'
import { buildWidgetDragPayload } from 'modules/desktop/modules/widgets/utils'
import { useAnimate, useReducedMotion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useGetAppsQuery } from 'shared/api/containerApi/requests'
import {
  useGetArtifactCategoriesQuery,
  useGetArtifactsQuery,
  useReviewerCapabilityQuery
} from 'shared/api/forgeApi'
import { WidgetKey, WidgetSize } from 'shared/constants/widgets'
import { useDebounce } from 'shared/hooks/useDebounce'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { useUserContext } from 'shared/hooks/useUserContext'
import { AiArtifactConfig } from 'shared/types/widgets'
import { WidgetSpec } from 'shared/types/widgets'

import { WIDGET_DATA_TRANSFER_TYPE } from '../../MainPage.constants'
import { DragContext, setDragContext } from '../../stores/dragStore'
import { useWindowStore } from '../../stores/windowStore'
import { useTouchDragWithGhost } from '../../utils/useTouchDragWithGhost'
import { useTouchEnvironment } from '../../utils/useTouchEnvironment'
import { CategoryNavigation } from './CategoryNavigation/CategoryNavigation'
import { WIDGET_MARKETPLACE_WINDOW_ID } from './WidgetMarketplace.constants'
import styles from './WidgetMarketplace.module.scss'
import type { WidgetMarketplaceProps } from './WidgetMarketplace.types'
import {
  buildMarketplaceWidgets,
  createAiArtifactDragGhost,
  createAiArtifactNativeDragImage
} from './helpers'
import {
  buildMarketplaceCategoryGroups,
  categoryForRelatedApp
} from './helpers/marketplaceCategories'
import { useWidgetMarketplaceFilterStore } from './widgetMarketplaceFilterStore'

const SEARCH_DEBOUNCE_MS = 300

interface MarketplaceSearchProps {
  ariaLabel: string
  placeholder: string
  onQueryChange: (query: string) => void
}

const MarketplaceSearch = React.memo<MarketplaceSearchProps>(
  ({ ariaLabel, placeholder, onQueryChange }) => {
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearchQuery = useDebounce(
      searchQuery.trim(),
      SEARCH_DEBOUNCE_MS
    )

    useEffect(() => {
      onQueryChange(debouncedSearchQuery.toLocaleLowerCase())
    }, [debouncedSearchQuery, onQueryChange])

    return (
      <div className={styles.search}>
        <TextInput
          aria-label={ariaLabel}
          placeholder={placeholder}
          size={TextInputSizes.M}
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          onClear={() => setSearchQuery('')}
          icon={MagnifyerLineIcon}
          clearable
        />
      </div>
    )
  }
)

export const WidgetMarketplace: FC<WidgetMarketplaceProps> = ({
  initialRelatedApp
}) => {
  const { user } = useUserContext()
  const { data: containerAppsData = [] } = useGetAppsQuery()
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(() =>
    categoryForRelatedApp(initialRelatedApp, user)
  )
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [normalizedSearchQuery, setNormalizedSearchQuery] = useState('')
  const [searchResetKey, setSearchResetKey] = useState(0)
  const [, startSearchTransition] = useTransition()
  const handleSearchQueryChange = useCallback(
    (query: string) => {
      startSearchTransition(() => setNormalizedSearchQuery(query))
    },
    [startSearchTransition]
  )

  const pendingRelatedApp = useWidgetMarketplaceFilterStore(
    state => state.pendingRelatedApp
  )
  const pendingSection = useWidgetMarketplaceFilterStore(
    state => state.pendingSection
  )
  const setPendingRelatedApp = useWidgetMarketplaceFilterStore(
    state => state.setPendingRelatedApp
  )
  const setPendingSection = useWidgetMarketplaceFilterStore(
    state => state.setPendingSection
  )

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (pendingRelatedApp === null && pendingSection === null) return

    if (pendingSection === AiAgentSection.REVIEW) {
      setIsReviewOpen(true)
    } else if (pendingRelatedApp !== null) {
      setIsReviewOpen(false)
      setActiveCategoryId(categoryForRelatedApp(pendingRelatedApp, user))
    }
    setPendingRelatedApp(null)
    setPendingSection(null)
  }, [
    pendingRelatedApp,
    pendingSection,
    user,
    setPendingRelatedApp,
    setPendingSection
  ])
  const { items: sidebarItems } = useDesktopWidgets()
  const isTouchEnvironment = useTouchEnvironment()
  const [draggingWidgetId, setDraggingWidgetId] = useState<string | null>(null)
  const currentDragDataRef = useRef<{
    widgetKey: WidgetKey
    size: WidgetSize
    element: HTMLElement
    // per-instance config so touch drag lands with the right publicId
    config?: AiArtifactConfig
  } | null>(null)

  const { isDesktop } = useMediaQuery()
  const { t: tMarketplace } = useTranslation('translation', {
    keyPrefix: 'widgets.marketplace'
  })

  const {
    data: aiArtifacts,
    isInitialLoading: isLoadingAiArtifacts,
    isError: artifactsError,
    refetch: retryArtifacts
  } = useGetArtifactsQuery(undefined, { kind: 'widget' })
  const {
    data: categories,
    isInitialLoading: isLoadingCategories,
    isError: categoriesError,
    refetch: retryCategories
  } = useGetArtifactCategoriesQuery()

  const { data: reviewerCapability } = useReviewerCapabilityQuery()
  const isReviewer = Boolean(reviewerCapability?.is_reviewer)
  const showReviewView = isReviewer && isReviewOpen

  const createAiWidget = () =>
    openAgentChat('Create a widget for my workspace that shows ', {
      mode: 'code'
    })

  const minimizeWindow = useWindowStore(state => state.minimizeWindow)
  const updateWindow = useWindowStore(state => state.updateWindow)
  const setActiveWindow = useWindowStore(state => state.setActiveWindow)

  const wasHiddenByDragRef = useRef(false)
  const shouldHideWhileDragging = !isDesktop

  const marketplaceWidgets = useMemo(
    () => buildMarketplaceWidgets(null, user),
    [user]
  )
  const categoryGroups = useMemo(
    () =>
      buildMarketplaceCategoryGroups(
        (categories ?? []).map(category => ({
          ...category,
          name: tMarketplace(`categoryNames.${category.id}`, {
            defaultValue: category.name
          })
        })),
        marketplaceWidgets,
        aiArtifacts ?? [],
        tMarketplace('other')
      ),
    [categories, marketplaceWidgets, aiArtifacts, tMarketplace]
  )
  // A removed or newly empty category returns to All after data refresh.
  const selectedCategoryId = categoryGroups.some(
    group => group.id === activeCategoryId
  )
    ? activeCategoryId
    : null
  const filteredGroups = categoryGroups
    .filter(
      group => selectedCategoryId === null || group.id === selectedCategoryId
    )
    .map(group => ({
      ...group,
      widgets: group.widgets.filter(widget =>
        WIDGETS_CONFIG[widget.widgetKey].name
          .toLocaleLowerCase()
          .includes(normalizedSearchQuery)
      ),
      artifacts: group.artifacts.filter(artifact =>
        artifact.title.toLocaleLowerCase().includes(normalizedSearchQuery)
      )
    }))
    .filter(group => group.widgets.length || group.artifacts.length)
  const hasSearchResults = filteredGroups.length > 0
  const [resultsScope, animateResults] = useAnimate<HTMLDivElement>()
  const reduceMotion = useReducedMotion()

  // Animate the existing view rather than keying/remounting widget iframes.
  useEffect(() => {
    if (reduceMotion || !resultsScope.current) return
    const animation = animateResults(
      resultsScope.current,
      { opacity: [0.65, 1], y: [6, 0] },
      { duration: 0.2, ease: 'easeOut' }
    )
    return () => {
      animation.cancel()
    }
  }, [selectedCategoryId, reduceMotion, animateResults, resultsScope])

  const doesWidgetAlreadyExist = (widgetKey: WidgetKey, _size: WidgetSize) =>
    sidebarItems.some(item => item.widgetKey === widgetKey)

  const handleDragStart =
    (
      widgetKey: WidgetKey,
      size: WidgetSize,
      config?: AiArtifactConfig,
      aiGhostLabel?: string
    ) =>
    (event: React.DragEvent<HTMLDivElement>) => {
      // A finger already owns this drag. Chrome on a Windows touchscreen can
      // still promote the long press to a native drag, which would drop the
      // widget twice.
      if (isTouchDragging) {
        event.preventDefault()
        return
      }

      if (shouldHideWhileDragging) {
        wasHiddenByDragRef.current = true

        requestAnimationFrame(() => {
          minimizeWindow(WIDGET_MARKETPLACE_WINDOW_ID)
        })
      }

      const payload = JSON.stringify(
        buildWidgetDragPayload(widgetKey, size, config)
      )
      event.dataTransfer.setData(WIDGET_DATA_TRANSFER_TYPE, payload)
      if (aiGhostLabel !== undefined) {
        // Safari can reject a drag session containing only a custom MIME type.
        event.dataTransfer.setData('text/plain', payload)
      }
      event.dataTransfer.effectAllowed = 'copy'

      const height = WIDGET_NATURAL_HEIGHTS[size]
      let dragImage: HTMLElement
      let waitForPaintBeforeRemoval = false

      if (aiGhostLabel !== undefined) {
        const sourceRect = event.currentTarget.getBoundingClientRect()
        dragImage = createAiArtifactNativeDragImage(
          aiGhostLabel,
          WIDGET_NATURAL_WIDTH,
          height,
          { left: sourceRect.left, top: sourceRect.top }
        )
        waitForPaintBeforeRemoval = true
      } else {
        dragImage = event.currentTarget.cloneNode(true) as HTMLElement
        dragImage.style.position = 'absolute'
        dragImage.style.top = '-9999px'
        dragImage.style.left = '-9999px'
        dragImage.style.width = `${WIDGET_NATURAL_WIDTH}px`
        dragImage.style.height = `${height}px`
        dragImage.style.opacity = '0.8'
        document.body.appendChild(dragImage)
      }

      event.dataTransfer.setDragImage(
        dragImage,
        WIDGET_NATURAL_WIDTH / 2,
        height / 2
      )

      requestAnimationFrame(() => {
        if (waitForPaintBeforeRemoval) {
          requestAnimationFrame(() => dragImage.remove())
        } else {
          dragImage.remove()
        }
      })

      setDragContext({
        source: 'marketplace',
        widgetKey,
        size,
        ...(config ? { config } : {})
      })
    }

  const handleDragEnd = () => {
    setDragContext(null)

    if (wasHiddenByDragRef.current) {
      wasHiddenByDragRef.current = false
      updateWindow(WIDGET_MARKETPLACE_WINDOW_ID, {
        isMinimized: false,
        isVisible: true
      })
      setActiveWindow(WIDGET_MARKETPLACE_WINDOW_ID)
    }
  }

  const artifactTouchGhostRef = useRef<HTMLElement | null>(null)
  const removeArtifactTouchGhost = () => {
    const ghost = artifactTouchGhostRef.current
    if (ghost && document.body.contains(ghost)) document.body.removeChild(ghost)
    artifactTouchGhostRef.current = null
  }

  const {
    handleTouchStart: baseHandleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isDragging: isTouchDragging
  } = useTouchDragWithGhost<DragContext>({
    element: document.createElement('div'),
    dragContext: {
      source: 'marketplace',
      widgetKey: '' as WidgetKey,
      size: DEFAULT_WIDGET_SIZE
    },
    ghostOptions: {
      width: WIDGET_NATURAL_WIDTH,
      height: WIDGET_NATURAL_HEIGHTS[DEFAULT_WIDGET_SIZE]
    },
    enabled: true,
    getCurrentDragData: () => {
      if (!currentDragDataRef.current) return null
      return {
        element: currentDragDataRef.current.element,
        dragContext: {
          source: 'marketplace',
          widgetKey: currentDragDataRef.current.widgetKey,
          size: currentDragDataRef.current.size,
          ...(currentDragDataRef.current.config
            ? { config: currentDragDataRef.current.config }
            : {})
        },
        ghostOptions: {
          width: WIDGET_NATURAL_WIDTH,
          height: WIDGET_NATURAL_HEIGHTS[currentDragDataRef.current.size]
        }
      }
    },
    onDragStart: () => {
      if (shouldHideWhileDragging) {
        wasHiddenByDragRef.current = true
        minimizeWindow(WIDGET_MARKETPLACE_WINDOW_ID)
      }
    },
    onDragEnd: () => {
      setDraggingWidgetId(null)
      currentDragDataRef.current = null
      removeArtifactTouchGhost()
      setDragContext(null)

      if (!wasHiddenByDragRef.current) return

      wasHiddenByDragRef.current = false
      updateWindow(WIDGET_MARKETPLACE_WINDOW_ID, {
        isMinimized: false,
        isVisible: true
      })
      setActiveWindow(WIDGET_MARKETPLACE_WINDOW_ID)
    }
  })

  const handleTouchStartForWidget =
    (widgetKey: WidgetKey, size: WidgetSize) =>
    (event: React.TouchEvent<HTMLDivElement>) => {
      const exists = doesWidgetAlreadyExist(widgetKey, size)
      if (exists) return

      currentDragDataRef.current = {
        widgetKey,
        size,
        element: event.currentTarget
      }
      setDraggingWidgetId(`${widgetKey}-${size}`)

      baseHandleTouchStart(event)
    }

  // same cross-origin constraint as above
  const handleTouchStartForArtifact =
    (publicId: string, size: WidgetSize, title: string) =>
    (event: React.TouchEvent<HTMLDivElement>) => {
      removeArtifactTouchGhost()
      const ghost = createAiArtifactDragGhost(
        title,
        WIDGET_NATURAL_WIDTH,
        WIDGET_NATURAL_HEIGHTS[size]
      )
      artifactTouchGhostRef.current = ghost

      currentDragDataRef.current = {
        widgetKey: WidgetKey.AI_ARTIFACT_TILE,
        size,
        element: ghost,
        config: { publicId }
      }
      setDraggingWidgetId(`ai:${publicId}`)

      baseHandleTouchStart(event)
    }

  const draggingArtifactPublicId = draggingWidgetId?.startsWith('ai:')
    ? draggingWidgetId.slice('ai:'.length)
    : null

  const renderPreview = (widget: WidgetSpec) => {
    const exists = doesWidgetAlreadyExist(widget.widgetKey, widget.size)
    const widgetId = `${widget.widgetKey}-${widget.size}`
    const isDragging = draggingWidgetId === widgetId

    const SidebarWidget = WIDGETS_CONFIG[widget.widgetKey].component
    const currentWidget = WIDGETS_CONFIG[widget.widgetKey]

    if (currentWidget.isHidden?.(user) || currentWidget.marketplaceHidden)
      return null

    const sharedWidgetProps: Omit<WidgetWrapperProps, 'children'> = {
      widget,
      isDragging,
      showInUseOverlay: exists
    }

    return (
      <MarketplaceTileShell
        key={widgetId}
        inUse={exists}
        isDragging={isDragging}
        name={`${WIDGETS_CONFIG[widget.widgetKey].name} ${widget.size}`}
        draggable={!exists}
        onContextMenu={e => {
          if (isTouchEnvironment) e.preventDefault()
        }}
        onDragStart={handleDragStart(widget.widgetKey, widget.size)}
        onDragEnd={handleDragEnd}
        onTouchStart={
          !exists
            ? handleTouchStartForWidget(widget.widgetKey, widget.size)
            : undefined
        }
        onTouchMove={
          draggingWidgetId === widgetId ? handleTouchMove : undefined
        }
        onTouchEnd={draggingWidgetId === widgetId ? handleTouchEnd : undefined}
        onTouchCancel={
          draggingWidgetId === widgetId ? handleTouchEnd : undefined
        }
      >
        <ViewportMount
          className={styles.widgetPreviewSlot}
          style={{
            aspectRatio: `${WIDGET_NATURAL_WIDTH}/${WIDGET_NATURAL_HEIGHTS[widget.size]}`,
            height: 'auto'
          }}
        >
          <WidgetsErrorBoundary
            orientation={
              widget.size === WidgetSize.SMALL ? 'horizontal' : 'vertical'
            }
            debugName={`Widget - ${currentWidget.name}`}
            {...sharedWidgetProps}
          >
            <div className={styles.widgetBoundaryContent}>
              <SidebarWidget {...sharedWidgetProps} />
            </div>
          </WidgetsErrorBoundary>
        </ViewportMount>
      </MarketplaceTileShell>
    )
  }

  return (
    <div
      className={styles.marketplace}
      onContextMenu={e => {
        if (isTouchEnvironment) {
          e.preventDefault()
        }
      }}
    >
      <CategoryNavigation
        categories={categoryGroups}
        activeCategoryId={selectedCategoryId}
        onSelect={id => {
          setIsReviewOpen(false)
          setActiveCategoryId(id)
          setNormalizedSearchQuery('')
          setSearchResetKey(key => key + 1)
          contentRef.current?.scrollTo({ top: 0 })
        }}
        onCreateWidget={
          hasAppAccess('agent', containerAppsData) ? createAiWidget : undefined
        }
        showReview={isReviewer}
        isReviewActive={showReviewView}
        onOpenReview={() => {
          setIsReviewOpen(true)
          setNormalizedSearchQuery('')
          setSearchResetKey(key => key + 1)
          contentRef.current?.scrollTo({ top: 0 })
        }}
      />
      <div className={styles.grid} ref={contentRef}>
        {showReviewView ? (
          <ReviewSection />
        ) : (
          <>
            <MarketplacePageHeader
              title={tMarketplace('browseTitle')}
              subtitle={tMarketplace('browseHint')}
            >
              <MarketplaceSearch
                key={searchResetKey}
                ariaLabel={tMarketplace('searchLabel')}
                placeholder={tMarketplace('searchPlaceholder')}
                onQueryChange={handleSearchQueryChange}
              />
            </MarketplacePageHeader>
            {(isLoadingAiArtifacts || isLoadingCategories) && (
              <div className={styles.feedback} role='status'>
                {tMarketplace('loading')}
              </div>
            )}
            {artifactsError && (
              <div className={styles.feedback} role='status'>
                {tMarketplace('widgetsError')}
                <Button
                  label={tMarketplace('retry')}
                  onClick={() => void retryArtifacts()}
                />
              </div>
            )}
            {categoriesError && (
              <div className={styles.feedback} role='status'>
                {tMarketplace('categoriesError')}
                <Button
                  label={tMarketplace('retry')}
                  onClick={() => void retryCategories()}
                />
              </div>
            )}
            <div ref={resultsScope}>
              {filteredGroups.map(group => (
                <section
                  className={styles.group}
                  key={group.id}
                  aria-label={group.name}
                >
                  <div className={styles.groupHeader}>
                    <h2 className={styles.groupLabel}>{group.name}</h2>
                    <span
                      className={styles.groupCount}
                      aria-label={tMarketplace('previewCount', {
                        count: group.widgets.length + group.artifacts.length
                      })}
                    >
                      {group.widgets.length + group.artifacts.length}
                    </span>
                  </div>
                  <div className={styles.gridInner}>
                    {group.widgets.map(renderPreview)}
                    <AiArtifactMarketplaceItems
                      artifacts={group.artifacts}
                      onCardDragStart={(publicId, size, title) =>
                        handleDragStart(
                          WidgetKey.AI_ARTIFACT_TILE,
                          size,
                          { publicId },
                          title
                        )
                      }
                      onCardDragEnd={handleDragEnd}
                      draggingPublicId={draggingArtifactPublicId}
                      onCardTouchStart={handleTouchStartForArtifact}
                      onCardTouchMove={handleTouchMove}
                      onCardTouchEnd={handleTouchEnd}
                    />
                  </div>
                </section>
              ))}
              {normalizedSearchQuery &&
                !isLoadingAiArtifacts &&
                !isLoadingCategories &&
                !artifactsError &&
                !hasSearchResults && (
                  <div className={styles.noResults} role='status'>
                    <EmptyState
                      title={tMarketplace('noResults')}
                      description={tMarketplace('noResultsHint')}
                      illustration={Illustrations.Magnifier}
                      size={EmptyStateSizes.Medium}
                      actions={[
                        {
                          label: tMarketplace('clearSearch'),
                          onClick: () => {
                            setNormalizedSearchQuery('')
                            setSearchResetKey(key => key + 1)
                          }
                        }
                      ]}
                    />
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
